import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Timezone from "common/core/form/timezone";
import SaveButton from "common/core/save_button";
import Button from "common/core/button";
import { Card, CardHeading, CardSection, CardText } from "common/core/card";
import { useForm } from "common/core/form";
import { Checkbox, CheckboxLabel } from "common/core/form/option";
import { FormRow } from "common/core/form/layout";
import { isAriaInvalid } from "common/core/form/error";
import { type Day, DAYS_OF_WEEK, MESSAGES } from "common/week_days";

import type {
  OrganizationSchedule_organization_Organization as Organization,
  OrganizationSchedule_organization_Organization_schedule_timeSlots as TimeSlot,
} from "./schedule_query.graphql";
import Time from "./time";
import Styles from "./form.module.scss";

export type FormValues = { [key in Day]: boolean } & {
  startTime: number;
  endTime: number;
  timezone: string;
};

type Props = {
  submitting: boolean;
  onSubmit: (values: FormValues) => Promise<void>;
  organization: Organization | null | undefined;
  userCanUpdate: boolean;
};

type FooterProps = {
  toggleShowForm: () => void;
  showForm: boolean;
  submitting: boolean;
  invalid: boolean;
};

function Footer({ toggleShowForm, showForm, submitting, invalid }: FooterProps) {
  if (showForm) {
    return (
      <div>
        <SaveButton
          disabled={submitting || invalid}
          isLoading={submitting}
          title={
            <FormattedMessage
              id="3cf6d86a-5f5d-4d05-9ce8-0c90f7d52607"
              defaultMessage="Save Changes"
            />
          }
        />
        <Button
          className={Styles.cancelBtn}
          onClick={toggleShowForm}
          buttonColor="action"
          variant="secondary"
        >
          <FormattedMessage id="64279b64-3383-4442-a4da-836d652fa509" defaultMessage="Cancel" />
        </Button>
      </div>
    );
  }
  return (
    <Button onClick={toggleShowForm} type="button" buttonColor="action" variant="secondary">
      <FormattedMessage
        id="bf2d9a92-0923-4dc3-9398-c316869ddd08"
        defaultMessage="Change Notary Hours"
      />
    </Button>
  );
}

export default function NotaryHoursForm({
  onSubmit,
  submitting,
  organization,
  userCanUpdate,
}: Props) {
  const intl = useIntl();
  const schedule = organization?.schedule;
  const [showForm, setShowForm] = useState(false);
  const handleSubmit = async (formValues: FormValues) => {
    await onSubmit(formValues);
    setShowForm(false);
  };
  const days: { [key in Day]?: boolean } = schedule
    ? Object.fromEntries(schedule.timeSlots.map(({ day }) => [day, true]))
    : Object.fromEntries(DAYS_OF_WEEK.slice(1, 6).map((day) => [day, true]));

  const form = useForm<FormValues>({
    defaultValues: {
      timezone: schedule?.timezone || organization?.owner?.timezone || "America/New_York",
      // start time represented as minuets since the start of the day. Default to 9:00 AM
      startTime: schedule?.timeSlots[0].startTime || 540,
      // end time represented as minuets since the start of the day. Default to 5:00 PM
      endTime: schedule?.timeSlots[0].endTime || 1020,
      ...days,
    },
  });
  const { errors } = form.formState;
  const timeslots = schedule?.timeSlots;
  const scheduleTimezone = schedule?.timezone;
  const { daysDisplay, hoursDisplay } = useMemo(() => {
    if (!timeslots?.length) {
      return { daysDisplay: null, hoursDisplay: null };
    }
    const [firstTimeslot] = timeslots;
    return {
      daysDisplay: timeslots
        .slice()
        .sort((a, b) => DAYS_OF_WEEK.indexOf(a.day as Day) - DAYS_OF_WEEK.indexOf(b.day as Day))
        .map((slot: TimeSlot) => intl.formatMessage(MESSAGES[slot.day as Day]))
        .join(", "),
      hoursDisplay: `${firstTimeslot.open} \u2013 ${firstTimeslot.close} ${
        scheduleTimezone?.replace("_", " ") || ""
      }`,
    };
  }, [timeslots, scheduleTimezone]);

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <Card
        footer={
          userCanUpdate && (
            <Footer
              showForm={showForm}
              toggleShowForm={() => setShowForm(!showForm)}
              submitting={submitting}
              invalid={false}
            />
          )
        }
      >
        <CardSection>
          <CardHeading>
            <div className={Styles.cardHeading}>
              <FormattedMessage
                id="ca6bc23a-ff59-46c6-b943-d1c3564718bf"
                defaultMessage="When can signers book meetings?"
              />
            </div>
          </CardHeading>
          <CardText>
            <FormattedMessage
              id="ab195001-6def-47cd-8d10-1e1dc1a68d98"
              defaultMessage="All bookable meeting times start on the half hour. Signers will not able to book a time that is less than 30 minutes from the meeting start time. For example, if it is currently 9:03 AM for the signer, the next bookable time will be 10:00 AM."
              tagName="p"
            />
          </CardText>
          {showForm ? (
            <div>
              <p className={Styles.formHeading}>
                <FormattedMessage
                  id="579d8191-8d42-4a96-9acc-580ef493d4f8"
                  defaultMessage="available hours"
                />
              </p>
              <div className={Styles.hoursInputGroup}>
                <Time
                  aria-invalid={isAriaInvalid(errors.startTime)}
                  {...form.register("startTime", {
                    required: true,
                  })}
                />
                <span className={Styles.timeSeparator} />
                <Time
                  aria-invalid={isAriaInvalid(errors.endTime)}
                  {...form.register("endTime", {
                    required: true,
                  })}
                />
              </div>
              <FormRow>
                <Timezone
                  aria-invalid={isAriaInvalid(errors.timezone)}
                  {...form.register("timezone", {
                    required: true,
                  })}
                />
              </FormRow>
              <p className={Styles.formHeading}>
                <FormattedMessage
                  id="579d8191-8d42-4a96-9acc-580ef493d4f8"
                  defaultMessage="available days"
                />
              </p>
              <p className={Styles.formNote}>
                <FormattedMessage
                  id="579d8191-8d42-4a96-9acc-580ef493d4f8"
                  defaultMessage="Note: Changing notary hours will not affect currently scheduled meetings."
                />
              </p>
              <div className={Styles.checkboxContainer}>
                <CheckboxLabel
                  label={intl.formatMessage(MESSAGES.monday)}
                  checkbox={
                    <Checkbox
                      aria-invalid={isAriaInvalid(errors.monday)}
                      {...form.register("monday")}
                    />
                  }
                />
              </div>
              <div className={Styles.checkboxContainer}>
                <CheckboxLabel
                  label={intl.formatMessage(MESSAGES.tuesday)}
                  checkbox={
                    <Checkbox
                      aria-invalid={isAriaInvalid(errors.tuesday)}
                      {...form.register("tuesday")}
                    />
                  }
                />
              </div>
              <div className={Styles.checkboxContainer}>
                <CheckboxLabel
                  label={intl.formatMessage(MESSAGES.wednesday)}
                  checkbox={
                    <Checkbox
                      aria-invalid={isAriaInvalid(errors.wednesday)}
                      {...form.register("wednesday")}
                    />
                  }
                />
              </div>
              <div className={Styles.checkboxContainer}>
                <CheckboxLabel
                  label={intl.formatMessage(MESSAGES.thursday)}
                  checkbox={
                    <Checkbox
                      aria-invalid={isAriaInvalid(errors.thursday)}
                      {...form.register("thursday")}
                    />
                  }
                />
              </div>
              <div className={Styles.checkboxContainer}>
                <CheckboxLabel
                  label={intl.formatMessage(MESSAGES.friday)}
                  checkbox={
                    <Checkbox
                      aria-invalid={isAriaInvalid(errors.friday)}
                      {...form.register("friday")}
                    />
                  }
                />
              </div>
              <div className={Styles.checkboxContainer}>
                <CheckboxLabel
                  label={intl.formatMessage(MESSAGES.saturday)}
                  checkbox={
                    <Checkbox
                      aria-invalid={isAriaInvalid(errors.saturday)}
                      {...form.register("saturday")}
                    />
                  }
                />
              </div>
              <div className={Styles.checkboxContainer}>
                <CheckboxLabel
                  label={intl.formatMessage(MESSAGES.sunday)}
                  checkbox={
                    <Checkbox
                      aria-invalid={isAriaInvalid(errors.sunday)}
                      {...form.register("sunday")}
                    />
                  }
                />
              </div>
            </div>
          ) : (
            <CardSection>
              <CardText>
                <div className={Styles.hoursBox}>
                  <p className={Styles.hoursHeading}>
                    <FormattedMessage
                      id="0400e959-5306-4ce6-b7c5-da82352c25e2"
                      defaultMessage="current hours"
                    />
                  </p>
                  {schedule ? (
                    <>
                      <p>{daysDisplay}</p>
                      <p>{hoursDisplay}</p>
                    </>
                  ) : (
                    <>
                      <p>
                        <FormattedMessage
                          id="78168d74-2023-4343-be30-382ae7e3e15a"
                          defaultMessage={`Monday \u2013 Friday`}
                        />
                      </p>
                      <p>
                        <FormattedMessage
                          id="fae56e35-69f3-47fe-8894-1bead4a1c924"
                          defaultMessage={`9:00AM \u2013 5:00 PM {timezone}`}
                          values={{
                            timezone: organization?.owner?.timezone?.replace("_", " ") || "",
                          }}
                        />
                      </p>
                    </>
                  )}
                </div>
              </CardText>
            </CardSection>
          )}
        </CardSection>
      </Card>
    </form>
  );
}
